<!--
  The one form in the product. It sends exactly what the user typed, trimmed,
  and lets the server's `userErrors` be the source of truth on what a valid
  target looks like — the server runs the same parser as the command line
  tool, and a second hostname validator here would eventually disagree with
  it. Client-side checks stay to the three things worth catching before a round
  trip: an empty field, one that is obviously too long, and a port sweep asked
  for without the assertion that goes with it.

  The port sweep sits behind a disclosure that starts closed, and closing it
  puts both of its controls back to their defaults. Almost everybody arriving
  here wants a website scanned and should not have to walk past a
  port-scanning control to get one, and a collapsed disclosure is then a
  guarantee rather than a default: with it closed there is no profile and no
  assertion to send.
-->
<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { execute, TransportError } from '@/graphql/client'
import { RequestScanDocument } from '@/graphql/generated/graphql'
import type { PortProfile, RequestScanInput } from '@/graphql/generated/graphql'
import { PORT_PROFILE_OPTIONS } from '@/lib/portProfiles'
import UserErrorNotice from './UserErrorNotice.vue'

interface NoticeError {
  readonly code: string
  readonly message: string
  readonly path: readonly string[]
}

const TARGET_MAX_LENGTH = 255

const router = useRouter()

const target = ref('')
const authorized = ref(false)
const submitting = ref(false)
const rateLimited = ref(false)
const isTransportError = ref(false)

/**
 * The port sweep controls.
 *
 * The empty string is "no sweep", which is what `RequestScanInput.portProfile`
 * being null means, and it is the value of the first option so a visitor who
 * opened the disclosure out of curiosity can put it back. `portAuthorized`
 * starts false and nothing persists it: the schema asks for the assertion to be
 * a deliberate act rather than a constant the client sends, and a box that
 * remembered last time's tick would be exactly the constant it warns about.
 */
const sweepOpen = ref(false)
const portProfile = ref<PortProfile | ''>('')
const portAuthorized = ref(false)

const targetError = ref<string | null>(null)
const authorizedError = ref<string | null>(null)
const portAuthorizedError = ref<string | null>(null)
const serverErrors = ref<readonly NoticeError[]>([])
const retryAfterSeconds = ref<number | null>(null)

const targetEl = ref<HTMLInputElement | null>(null)
const authorizedEl = ref<HTMLInputElement | null>(null)
const portAuthorizedEl = ref<HTMLInputElement | null>(null)
const noticeEl = ref<{ focus: () => Promise<void> } | null>(null)

let rateLimitTimer: ReturnType<typeof setTimeout> | undefined

function lastSegment(path: readonly string[]): string | undefined {
  return path[path.length - 1]
}

const targetInvalid = computed(
  () => Boolean(targetError.value) || serverErrors.value.some((e) => lastSegment(e.path) === 'target'),
)
const authorizedInvalid = computed(
  () =>
    Boolean(authorizedError.value) ||
    serverErrors.value.some((e) => lastSegment(e.path) === 'confirmedAuthorized'),
)
const portProfileInvalid = computed(() =>
  serverErrors.value.some((e) => lastSegment(e.path) === 'portProfile'),
)
const portAuthorizedInvalid = computed(
  () =>
    Boolean(portAuthorizedError.value) ||
    serverErrors.value.some((e) => lastSegment(e.path) === 'confirmedPortScanAuthorized'),
)

function focusTarget() {
  targetEl.value?.focus()
}

function focusAuthorized() {
  authorizedEl.value?.focus()
}

function focusPortAuthorized() {
  portAuthorizedEl.value?.focus()
}

/**
 * Keeps the sweep controls honest when the disclosure closes.
 *
 * A ticked box behind a collapsed panel is a request nobody can see they are
 * making, so closing the panel clears both controls. It costs somebody who
 * collapsed it by accident one re-pick, and it means the only way to ask for a
 * sweep is to have the control open in front of you.
 */
function onSweepToggle(event: Event) {
  sweepOpen.value = (event.target as HTMLDetailsElement).open
  if (sweepOpen.value) return
  portProfile.value = ''
  portAuthorized.value = false
  portAuthorizedError.value = null
}

async function focusNotice() {
  await nextTick()
  await noticeEl.value?.focus()
}

function startRateLimit(seconds: number) {
  rateLimited.value = true
  window.clearTimeout(rateLimitTimer)
  rateLimitTimer = window.setTimeout(() => {
    rateLimited.value = false
    serverErrors.value = []
    retryAfterSeconds.value = null
  }, seconds * 1000)
}

onUnmounted(() => window.clearTimeout(rateLimitTimer))

function applyServerErrors(errors: readonly NoticeError[], retry: number | null) {
  serverErrors.value = errors
  retryAfterSeconds.value = retry
  const limited = errors.some((e) => e.code === 'RATE_LIMITED' || e.code === 'CAPACITY_EXCEEDED')
  if (limited && typeof retry === 'number') startRateLimit(retry)
}

/**
 * The mutation input for one submission.
 *
 * With no profile chosen the two sweep fields are left out of the object
 * entirely rather than sent as null and false. Both have those values as their
 * schema defaults, so omitting them says the same thing, and it keeps the
 * ordinary request — which is nearly every request — to the two fields it has
 * always carried.
 */
function buildInput(trimmedTarget: string, profile: PortProfile | ''): RequestScanInput {
  const input: RequestScanInput = { target: trimmedTarget, confirmedAuthorized: true }
  if (profile === '') return input
  return { ...input, portProfile: profile, confirmedPortScanAuthorized: true }
}

async function requestScan(trimmedTarget: string) {
  submitting.value = true
  serverErrors.value = []
  retryAfterSeconds.value = null
  isTransportError.value = false
  // Read once, before the await: this is the profile that was submitted, and it
  // is what the report page is told about further down.
  const profile = portProfile.value
  try {
    const data = await execute(RequestScanDocument, { input: buildInput(trimmedTarget, profile) })
    const { scan, userErrors, retryAfterSeconds: retry } = data.requestScan
    if (scan) {
      // `portProfile` rides along in history state next to the target, and only
      // when a sweep was asked for. The report page uses it to hold a row open
      // for the port findings that are coming; its absence is "this page did not
      // make the request", which is also what a shared link looks like, and the
      // report reads the same either way.
      await router.push({
        name: 'scan',
        params: { id: scan.id },
        state: profile === '' ? { target: scan.target } : { target: scan.target, portProfile: profile },
      })
      return
    }
    applyServerErrors(userErrors, retry)
    await focusNotice()
  } catch (error) {
    if (!(error instanceof TransportError)) throw error
    isTransportError.value = true
    applyServerErrors([{ code: 'TRANSPORT_ERROR', message: error.userMessage, path: [] }], null)
    await focusNotice()
  } finally {
    submitting.value = false
  }
}

async function onSubmit() {
  if (submitting.value || rateLimited.value) return

  const trimmed = target.value.trim()
  targetError.value = null
  authorizedError.value = null
  portAuthorizedError.value = null
  serverErrors.value = []
  retryAfterSeconds.value = null
  isTransportError.value = false

  if (trimmed.length === 0) {
    targetError.value = 'Enter a hostname to scan.'
    focusTarget()
    return
  }
  if (trimmed.length > TARGET_MAX_LENGTH) {
    targetError.value = 'That is too long to be a hostname.'
    focusTarget()
    return
  }
  if (!authorized.value) {
    authorizedError.value = 'Confirm you are authorized before running a scan.'
    focusAuthorized()
    return
  }
  // The server refuses this pairing with PORT_SCAN_AUTHORIZATION_NOT_CONFIRMED
  // and starts no scan, so there is nothing to learn from the round trip. Caught
  // here it lands on the box that needs ticking instead.
  if (portProfile.value !== '' && !portAuthorized.value) {
    portAuthorizedError.value = 'Confirm you are authorized before sweeping ports on this host.'
    focusPortAuthorized()
    return
  }

  await requestScan(trimmed)
}

async function onCorrect(host: string) {
  target.value = host
  serverErrors.value = []
  retryAfterSeconds.value = null
  isTransportError.value = false
  await requestScan(host)
  focusTarget()
}

function onRetryTransport() {
  void requestScan(target.value.trim())
}
</script>

<template>
  <form
    novalidate
    @submit.prevent="onSubmit"
  >
    <Transition name="panel">
      <div
        v-if="serverErrors.length > 0"
        class="mb-5 flex flex-col gap-3"
      >
        <UserErrorNotice
          ref="noticeEl"
          :errors="serverErrors"
          :retry-after-seconds="retryAfterSeconds"
          :typed-target="target"
          @correct="onCorrect"
        />
        <button
          v-if="isTransportError"
          type="button"
          class="tap w-fit rounded-md border border-line-strong px-3 font-medium text-ink transition-colors duration-100 hover:bg-sunken active:translate-y-px"
          @click="onRetryTransport"
        >
          Try again
        </button>
      </div>
    </Transition>

    <div class="flex flex-col gap-1">
      <label
        for="target"
        class="font-medium text-ink"
      >Website to scan</label>
      <p
        id="target-help"
        class="text-sm text-ink-muted"
      >
        A hostname like example.com. A full URL works too.
      </p>
      <input
        id="target"
        ref="targetEl"
        v-model="target"
        type="text"
        name="target"
        inputmode="url"
        autocomplete="url"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        enterkeyhint="go"
        required
        maxlength="255"
        :aria-describedby="targetError ? 'target-help target-error' : 'target-help'"
        :aria-invalid="targetInvalid ? 'true' : undefined"
        class="tap w-full rounded-md border border-line bg-surface px-3 font-mono text-ink focus-visible:border-accent"
      >
      <p
        v-if="targetError"
        id="target-error"
        role="alert"
        data-tone="high"
        class="tone-ink text-sm font-medium"
      >
        {{ targetError }}
      </p>
    </div>

    <div class="mt-5 flex flex-col gap-1">
      <label
        for="authorized"
        class="tap flex items-center gap-2 text-ink"
      >
        <input
          id="authorized"
          ref="authorizedEl"
          v-model="authorized"
          type="checkbox"
          name="authorized"
          aria-describedby="authorized-help"
          :aria-invalid="authorizedInvalid ? 'true' : undefined"
          class="h-5 w-5 shrink-0 rounded border-line-strong"
        >
        I am authorized to scan this target.
      </label>
      <p
        id="authorized-help"
        class="text-sm text-ink-muted"
      >
        Scanning opens connections and sends DNS queries to this host. Only scan infrastructure you
        run or have written permission to test.
      </p>
      <p
        v-if="authorizedError"
        role="alert"
        data-tone="high"
        class="tone-ink text-sm font-medium"
      >
        {{ authorizedError }}
      </p>
    </div>

    <!-- A native disclosure, so it opens on Enter and Space and announces its
         state without any of that being reimplemented here. `:open` is bound
         so the reset in `onSweepToggle` has somewhere to reset to. -->
    <details
      class="mt-5 rounded-md border border-line"
      :open="sweepOpen"
      @toggle="onSweepToggle"
    >
      <summary class="tap flex cursor-pointer items-center gap-2 px-3 font-medium text-ink select-none">
        Also sweep ports
        <svg
          class="chevron ml-auto transition-transform duration-150 motion-reduce:transition-none"
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M6 4 L10 8 L6 12" />
        </svg>
      </summary>

      <div class="flex flex-col gap-4 border-t border-line p-3">
        <p
          id="port-sweep-help"
          class="text-sm text-ink-muted"
        >
          A sweep opens a TCP connection to every port in the profile you choose, on every address
          this name resolves to. That reaches further into the host than a website scan does, and
          the far end will see it as a port scan.
        </p>

        <div class="flex flex-col gap-1">
          <label
            for="port-profile"
            class="font-medium text-ink"
          >Ports to sweep</label>
          <select
            id="port-profile"
            v-model="portProfile"
            name="port-profile"
            aria-describedby="port-sweep-help"
            :aria-invalid="portProfileInvalid ? 'true' : undefined"
            class="tap w-full rounded-md border border-line bg-surface px-3 text-ink focus-visible:border-accent"
          >
            <option value="">
              No port sweep
            </option>
            <option
              v-for="option in PORT_PROFILE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}: {{ option.reach }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label
            for="port-authorized"
            class="tap flex items-center gap-2 text-ink"
          >
            <input
              id="port-authorized"
              ref="portAuthorizedEl"
              v-model="portAuthorized"
              type="checkbox"
              name="port-authorized"
              :aria-describedby="
                portAuthorizedError ? 'port-authorized-help port-authorized-error' : 'port-authorized-help'
              "
              :aria-invalid="portAuthorizedInvalid ? 'true' : undefined"
              class="h-5 w-5 shrink-0 rounded border-line-strong"
            >
            I am authorized to have this host&apos;s ports swept.
          </label>
          <p
            id="port-authorized-help"
            class="text-sm text-ink-muted"
          >
            A separate claim from the one above, which covers reading this host as a website. This
            one covers connecting to ports nobody published. Both are recorded with the request.
          </p>
          <p
            v-if="portAuthorizedError"
            id="port-authorized-error"
            role="alert"
            data-tone="high"
            class="tone-ink text-sm font-medium"
          >
            {{ portAuthorizedError }}
          </p>
        </div>
      </div>
    </details>

    <button
      type="submit"
      :disabled="submitting || rateLimited"
      :aria-busy="submitting"
      class="btn-primary mt-6 w-full"
    >
      <svg
        v-if="submitting"
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="animate-spin motion-reduce:animate-none"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx="8"
          cy="8"
          r="6"
          stroke-dasharray="28"
          stroke-dashoffset="10"
          stroke-linecap="round"
        />
      </svg>
      {{ submitting ? 'Starting the scan…' : 'Run the scan' }}
    </button>
  </form>
</template>

<style scoped>
/* No :style binding, per the house rule: data-tone sets the token, this class
   just applies it. */
.tone-ink {
  color: var(--tone-ink);
}

/* The [open] boolean attribute is the state; this just reads it in CSS, the
   same way PassGroup does. */
details[open] .chevron {
  transform: rotate(90deg);
}
</style>
